import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';

import { useAuth } from '@/components/auth/AuthProvider';
import { useNotification } from '@/components/ui-library/NotificationProvider';
import {
  DEFAULT_LANGUAGE,
  resolveSupportedLanguage,
  type SupportedLanguageCode,
} from '@/constants/languages';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import useAnalytics from '@/hooks/useAnalytics';
import { useNavMode, setNavMode, applyNavModeFromServer, type NavMode } from '@/hooks/useNavMode';
import { answerStyleStore } from '@/hooks/useAnswerStyle';
import { isAnswerStyleEnabled } from '@/config/env';
import useApi from '@/hooks/useApi';
import { useProfileForm } from '@/hooks/useProfileForm';
import { changeLanguage, urlLanguageOverride } from '@/i18n';
import { ContextService } from '@/services/contextService';
import { formatAddress } from '@/utils/addressFormat';
import type { SaveState } from '@/components/settings/SaveStatus';

/**
 * Groups that report their own save state. Text groups (`name`, `address`,
 * `phone`) commit through an inline Save row; the rest are switches and
 * single-selects that write the moment they change.
 */
export type SaveGroup =
  | 'name'
  | 'address'
  | 'phone'
  | 'emailConsent'
  | 'phoneConsent'
  | 'autoSaveBallots'
  | 'language'
  | 'navMode';

interface GroupStatus {
  state: SaveState;
  error: string | null;
}

const IDLE_STATUS: GroupStatus = { state: 'idle', error: null };

const INITIAL_STATUSES: Record<SaveGroup, GroupStatus> = {
  name: IDLE_STATUS,
  address: IDLE_STATUS,
  phone: IDLE_STATUS,
  emailConsent: IDLE_STATUS,
  phoneConsent: IDLE_STATUS,
  autoSaveBallots: IDLE_STATUS,
  language: IDLE_STATUS,
  navMode: IDLE_STATUS,
};

// How long a "Saved" confirmation stays on screen before fading back to idle.
// Long enough to register, short enough that it never reads as a live state.
const SAVED_VISIBLE_MS = 2400;

const formatPhoneNumber = (raw: string): string => {
  const digits = (raw || '').replace(/\D/g, '');
  const limited = (digits.startsWith('1') ? digits.slice(1) : digits).slice(0, 10);
  const area = limited.slice(0, 3);
  const exchange = limited.slice(3, 6);
  const line = limited.slice(6, 10);
  if (limited.length <= 3) return area;
  if (limited.length <= 6) return `(${area}) ${exchange}`;
  return `(${area}) ${exchange}-${line}`;
};

// Best-effort split of an OIDC `name` claim into first/last when given_name/family_name
// aren't present (some social IdPs only return the combined name).
const splitOidcName = (full?: string): { first: string; last: string } => {
  const trimmed = (full || '').trim();
  if (!trimmed || trimmed.includes('@')) return { first: '', last: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

/**
 * Owns the Settings page's save model: which slice of the form is dirty, what
 * each slice's write is doing right now, and how it reaches the server.
 *
 * Each group writes only the fields it owns, so two groups saved seconds apart
 * never clobber each other, and the page needs no page-level "unsaved changes"
 * state at all.
 */
export const useSettingsSections = () => {
  const { t, i18n } = useTranslation('settings');
  const { user, updateUser, getAccessTokenSilently, refreshUserProfile } = useAuth();
  const { user: auth0User } = useAuth0();
  const { showNotification } = useNotification();
  const analytics = useAnalytics();
  const query = useApi();
  const { syncAutoSaveEnabled, refreshRecords: refreshBallotRecords } = useBallotRecords();

  // Resolved, not membership-tested, so a stored `zh-tw` lands on the `zh-Hant`
  // row the picker actually renders (FR-3).
  const resolvedLanguage: SupportedLanguageCode =
    resolveSupportedLanguage(i18n.language) ?? DEFAULT_LANGUAGE;

  const profileForm = useProfileForm();
  const {
    phone,
    address,
    phoneConsent,
    hasValidAddress,
    hasValidPhone,
    selectedAddressDetails,
    handleAddressChange,
    handlePhoneChange,
    setEmailConsent,
    setPhoneConsent,
    setAddressError,
    setPhoneError,
  } = profileForm;

  // Persisted ("saved") state — tracks what's in the database
  const [savedAddress, setSavedAddress] = useState('');
  const [savedPhone, setSavedPhone] = useState('');
  const [savedPhoneConsent, setSavedPhoneConsent] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savedFirstName, setSavedFirstName] = useState('');
  const [savedLastName, setSavedLastName] = useState('');
  const [language, setLanguage] = useState(resolvedLanguage);
  // Reflect a language change made elsewhere (e.g. the header chip) in the
  // dropdown; selecting here updates i18n, which lands back here too.
  useEffect(() => {
    setLanguage(resolvedLanguage);
  }, [resolvedLanguage]);
  const navMode = useNavMode();
  const [autoSaveBallots, setAutoSaveBallots] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [statuses, setStatuses] = useState<Record<SaveGroup, GroupStatus>>(INITIAL_STATUSES);

  // Server-truth consent, mirrored in a ref so two flags flipped in quick
  // succession compose. Reading React state here would let the second write
  // ship a stale snapshot of the other flag and silently undo the first.
  const consentRef = useRef({ email: false, phone: false });

  // Section saves fire independently, so two can be in flight at once. Chaining
  // them means the server applies them in the order the user made them and the
  // later response is always the newer decision — without this, a slow first
  // request can land last and undo the second.
  const writeQueue = useRef<Promise<unknown>>(Promise.resolve());

  // One pending "Saved → idle" timer per group, cleared on unmount so leaving
  // the page mid-save doesn't set state on an unmounted component.
  const savedTimers = useRef<Partial<Record<SaveGroup, ReturnType<typeof setTimeout>>>>({});
  useEffect(
    () => () => {
      Object.values(savedTimers.current).forEach((timer) => timer && clearTimeout(timer));
    },
    []
  );

  const setStatus = useCallback((group: SaveGroup, next: GroupStatus) => {
    setStatuses((prev) => ({ ...prev, [group]: next }));
  }, []);

  const markSaved = useCallback(
    (group: SaveGroup) => {
      setStatus(group, { state: 'saved', error: null });
      const existing = savedTimers.current[group];
      if (existing) clearTimeout(existing);
      savedTimers.current[group] = setTimeout(() => {
        setStatus(group, IDLE_STATUS);
        delete savedTimers.current[group];
      }, SAVED_VISIBLE_MS);
    },
    [setStatus]
  );

  const loadUserSettings = useCallback(async () => {
    const answerStyleRevision = answerStyleStore.accountRevision();
    try {
      const settingsResponse = await query('/user/settings');
      const settingsJson = await settingsResponse.json();
      const settings = settingsJson.data;
      if (settings) {
        // The server returns ``address_components`` only — derive the
        // display string here so no formatted copy lives on the wire.
        const formattedAddress = formatAddress(settings.address_components);
        const consent = settings.consent || {};
        const emailMarketing = !!consent.email_marketing;
        const phoneMarketing = !!consent.phone_marketing;
        handleAddressChange(formattedAddress);
        handlePhoneChange(settings.phone_number || '');
        setEmailConsent(emailMarketing);
        setPhoneConsent(phoneMarketing);
        setSavedPhoneConsent(phoneMarketing);
        consentRef.current = { email: emailMarketing, phone: phoneMarketing };
        setSavedAddress(formattedAddress);
        setSavedPhone(formatPhoneNumber(settings.phone_number || ''));
        // Pre-fill name from saved PII; for users who signed up before name was
        // collected (e.g. legacy social sign-ins) fall back to the OIDC claims so
        // the field isn't blank and a save doesn't accidentally wipe their display name.
        const oidcSplit = splitOidcName(auth0User?.name);
        const displayFirst = settings.first_name || auth0User?.given_name || oidcSplit.first || '';
        const displayLast = settings.last_name || auth0User?.family_name || oidcSplit.last || '';
        setFirstName(displayFirst);
        setLastName(displayLast);
        setSavedFirstName(displayFirst);
        setSavedLastName(displayLast);

        setAutoSaveBallots(!!settings.auto_save_ballots);

        // An account saved before canonical casing landed can hold `zh-hans`:
        // no matching row, same locale.
        const backendLanguage = resolveSupportedLanguage(settings.preferred_language ?? null);
        if (backendLanguage) {
          setLanguage(backendLanguage);
          // Reflect the saved account language in the dropdown, but when an inbound
          // `?lang=` is winning the session, leave the active UI language alone.
          if (!urlLanguageOverride && backendLanguage !== i18n.language) {
            changeLanguage(backendLanguage);
          }
        }

        // Seed the saved mobile-navigation preference so the account choice
        // follows the user across devices (no-op when the account has none).
        applyNavModeFromServer((settings as { nav_mode?: unknown }).nav_mode);
        // Refresh the account's answer style too; a save made while this read
        // was in flight is newer and wins.
        const accountId = user?.user_id ?? user?.id;
        if (isAnswerStyleEnabled() && accountId) {
          void answerStyleStore.applyAccount(accountId, settings, answerStyleRevision);
        }
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
      showNotification(t('errorLoadingSettings'), 'error');
    } finally {
      setSettingsLoaded(true);
    }
  }, [
    auth0User?.name,
    auth0User?.given_name,
    auth0User?.family_name,
    handleAddressChange,
    handlePhoneChange,
    query,
    setEmailConsent,
    setPhoneConsent,
    showNotification,
    t,
    i18n,
    user?.user_id,
    user?.id,
  ]);

  /**
   * POSTs one slice of settings. Every field the patch leaves out is skipped
   * server-side, so a group only ever writes what it owns.
   */
  const persist = useCallback(
    async (patch: Record<string, unknown>) => {
      const send = writeQueue.current
        .catch(() => {}) // a failed neighbour must not poison the chain
        .then(() =>
          query('/user/settings', {
            method: 'POST',
            body: JSON.stringify({ email: user!.email, ...patch }),
          })
        );
      writeQueue.current = send;
      await send;
    },
    [query, user]
  );

  /** Runs one group's write and drives its inline status through the states. */
  const runSave = useCallback(
    async (group: SaveGroup, work: () => Promise<void>): Promise<boolean> => {
      // Drop any pending "Saved → idle" timer from the previous save, so it
      // can't fire mid-flight and flip this one back to idle.
      const pending = savedTimers.current[group];
      if (pending) {
        clearTimeout(pending);
        delete savedTimers.current[group];
      }
      setStatus(group, { state: 'saving', error: null });
      try {
        await work();
        markSaved(group);
        return true;
      } catch {
        setStatus(group, { state: 'error', error: t('unableToSave') });
        return false;
      }
    },
    [markSaved, setStatus, t]
  );

  // ─── Name ──────────────────────────────────────────────────────────────────

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const nameDirty = trimmedFirstName !== savedFirstName || trimmedLastName !== savedLastName;

  const saveName = useCallback(
    () =>
      runSave('name', async () => {
        await persist({ first_name: trimmedFirstName, last_name: trimmedLastName });
        // Optimistically reflect the saved values locally so the popover and
        // greeting sync immediately — no extra /auth/callback round-trip.
        const resolvedName =
          trimmedFirstName || trimmedLastName
            ? `${trimmedFirstName} ${trimmedLastName}`.trim()
            : user?.name;
        updateUser({
          name: resolvedName,
          first_name: trimmedFirstName,
          last_name: trimmedLastName,
        });
        setSavedFirstName(trimmedFirstName);
        setSavedLastName(trimmedLastName);
        analytics.track('Settings Name Saved', { source: 'settings' });
      }),
    [runSave, persist, trimmedFirstName, trimmedLastName, updateUser, user?.name, analytics]
  );

  const cancelName = useCallback(() => {
    analytics.track('Settings Edit Cancelled', { section: 'name', source: 'settings' });
    setFirstName(savedFirstName);
    setLastName(savedLastName);
    setStatus('name', IDLE_STATUS);
  }, [savedFirstName, savedLastName, setStatus, analytics]);

  // ─── Address ───────────────────────────────────────────────────────────────

  const addressDirty = address !== savedAddress;
  const addressReady = addressDirty && hasValidAddress && !!selectedAddressDetails;

  const saveAddress = useCallback(async () => {
    if (!addressReady) {
      setAddressError(t('selectValidAddress'));
      return false;
    }
    return runSave('address', async () => {
      await persist({
        address_payload: {
          formatted_address: selectedAddressDetails!.formatted_address,
          address_components: selectedAddressDetails!.address_components,
        },
      });
      setSavedAddress(address);
      analytics.track('Settings Address Updated', { source: 'settings' });
      // The address change created a new draft ballot record server-side —
      // refresh the record list so the Ballots menu and status chip update,
      // and rebuild chat context, whose quick prompts depend on the ballot.
      void refreshBallotRecords();
      // The profile's derived ZIP/state/county feed the zip_code analytics
      // property, so reload it to follow the move.
      void refreshUserProfile();
      getAccessTokenSilently()
        .then((accessToken) => ContextService.initContext(accessToken))
        .catch(() => {});
    });
  }, [
    addressReady,
    setAddressError,
    t,
    runSave,
    persist,
    selectedAddressDetails,
    address,
    refreshBallotRecords,
    refreshUserProfile,
    getAccessTokenSilently,
    analytics,
  ]);

  const cancelAddress = useCallback(() => {
    analytics.track('Settings Edit Cancelled', { section: 'address', source: 'settings' });
    handleAddressChange(savedAddress);
    setAddressError(null);
    setStatus('address', IDLE_STATUS);
  }, [handleAddressChange, savedAddress, setAddressError, setStatus, analytics]);

  /**
   * Empties the address field and its saved baseline, so the no-ballots state
   * starts blank rather than showing the just-removed address.
   */
  const clearAddressField = useCallback(() => {
    handleAddressChange('');
    setSavedAddress('');
  }, [handleAddressChange]);

  // ─── Phone ─────────────────────────────────────────────────────────────────

  const phoneDirty = phone !== savedPhone;
  // Turning SMS on before a number exists stages the consent, so the phone
  // group carries that pending flip to the server along with the number.
  const consentPendingOnPhone = phoneConsent && !savedPhoneConsent;
  const phoneReady = (phoneDirty || consentPendingOnPhone) && hasValidPhone;

  const savePhone = useCallback(async () => {
    if (!hasValidPhone) {
      setPhoneError(t('enterValidPhone'));
      return false;
    }
    return runSave('phone', async () => {
      await persist({
        phone_number: phone,
        ...(consentPendingOnPhone && {
          consent: {
            email_marketing: consentRef.current.email,
            phone_marketing: true,
          },
        }),
      });
      setSavedPhone(phone);
      // Record the consent flip only now that the write landed; setting it up
      // front left the ref claiming SMS was on after a failed save.
      if (consentPendingOnPhone) {
        consentRef.current = { ...consentRef.current, phone: true };
        setSavedPhoneConsent(true);
      }
      analytics.track('Settings Phone Saved', {
        source: 'settings',
        enabledSms: consentPendingOnPhone,
      });
      if (consentPendingOnPhone) {
        // This path is how SMS actually gets enabled when no number was on
        // file, so it has to emit the same consent event the toggle does —
        // otherwise the opt-in funnel misses everyone who started from zero.
        analytics.track('Notification Consent Toggled', {
          channel: 'sms',
          enabled: true,
          source: 'settings',
        });
      }
    });
  }, [hasValidPhone, setPhoneError, t, runSave, persist, phone, consentPendingOnPhone, analytics]);

  const cancelPhone = useCallback(() => {
    analytics.track('Settings Edit Cancelled', { section: 'phone', source: 'settings' });
    handlePhoneChange(savedPhone);
    setPhoneError(null);
    // Backing out of the number also backs out the consent flip that revealed
    // it — including in the ref, which `savePhone` may already have set to true
    // on a failed attempt. Leaving it set would let the next email toggle
    // silently re-enable SMS on the server.
    if (consentPendingOnPhone || consentRef.current.phone !== savedPhoneConsent) {
      setPhoneConsent(false);
      consentRef.current = { ...consentRef.current, phone: savedPhoneConsent };
    }
    setStatus('phone', IDLE_STATUS);
  }, [
    handlePhoneChange,
    savedPhone,
    setPhoneError,
    consentPendingOnPhone,
    savedPhoneConsent,
    setPhoneConsent,
    setStatus,
    analytics,
  ]);

  // ─── Instant-save switches and selects ─────────────────────────────────────

  const toggleEmailConsent = useCallback(
    (next: boolean) => {
      const previous = consentRef.current.email;
      setEmailConsent(next);
      consentRef.current = { ...consentRef.current, email: next };
      void runSave('emailConsent', async () => {
        await persist({
          consent: {
            email_marketing: consentRef.current.email,
            phone_marketing: consentRef.current.phone,
          },
        });
        analytics.track('Notification Consent Toggled', {
          channel: 'email',
          enabled: next,
          source: 'settings',
        });
      }).then((ok) => {
        // A switch that silently stayed put is worse than one that snaps back.
        if (!ok) {
          setEmailConsent(previous);
          consentRef.current = { ...consentRef.current, email: previous };
        }
      });
    },
    [setEmailConsent, runSave, persist, analytics]
  );

  const togglePhoneConsent = useCallback(
    (next: boolean) => {
      setPhoneConsent(next);
      // Turning it on with no number on file only reveals the phone field —
      // the write rides along with `savePhone`.
      if (next && !hasValidPhone) {
        setStatus('phoneConsent', IDLE_STATUS);
        return;
      }
      const previous = consentRef.current.phone;
      consentRef.current = { ...consentRef.current, phone: next };
      void runSave('phoneConsent', async () => {
        await persist({
          consent: {
            email_marketing: consentRef.current.email,
            phone_marketing: consentRef.current.phone,
          },
          // Consent withdrawn means the number goes with it.
          ...(next ? {} : { phone_number: '' }),
        });
        setSavedPhoneConsent(next);
        analytics.track('Notification Consent Toggled', {
          channel: 'sms',
          enabled: next,
          source: 'settings',
        });
        if (!next) {
          handlePhoneChange('');
          setSavedPhone('');
          setPhoneError(null);
        }
      }).then((ok) => {
        if (!ok) {
          setPhoneConsent(previous);
          consentRef.current = { ...consentRef.current, phone: previous };
        }
      });
    },
    [
      setPhoneConsent,
      hasValidPhone,
      setStatus,
      runSave,
      persist,
      handlePhoneChange,
      setPhoneError,
      analytics,
    ]
  );

  const toggleAutoSaveBallots = useCallback(
    (next: boolean) => {
      const previous = autoSaveBallots;
      setAutoSaveBallots(next);
      void runSave('autoSaveBallots', async () => {
        await persist({ auto_save_ballots: next });
        // Sync the shared value (no second POST) so the ballot status popover
        // toggle reflects the change immediately instead of staying stale.
        syncAutoSaveEnabled(next);
        analytics.track('Ballot Auto Save Toggled', { enabled: next, source: 'settings' });
      }).then((ok) => {
        if (!ok) setAutoSaveBallots(previous);
      });
    },
    [autoSaveBallots, runSave, persist, syncAutoSaveEnabled, analytics]
  );

  const selectLanguage = useCallback(
    (code: SupportedLanguageCode) => {
      const previous = language;
      if (code === previous) return;
      setLanguage(code);
      changeLanguage(code);
      void runSave('language', async () => {
        await persist({ preferred_language: code });
        analytics.track('Language Updated', { source: 'settings', language: code });
      }).then((ok) => {
        if (!ok) {
          setLanguage(previous);
          changeLanguage(previous);
        }
      });
    },
    [language, runSave, persist, analytics]
  );

  const selectNavMode = useCallback(
    (mode: NavMode) => {
      const previous = navMode;
      if (mode === previous) return;
      setNavMode(mode);
      void runSave('navMode', async () => {
        await persist({ nav_mode: mode });
        analytics.track('Nav Mode Selected', { mode, previousMode: previous, authed: true });
      }).then((ok) => {
        if (!ok) setNavMode(previous);
      });
    },
    [navMode, runSave, persist, analytics]
  );

  const groups = useMemo(
    () => ({
      name: { ...statuses.name, dirty: nameDirty, canSave: nameDirty },
      address: { ...statuses.address, dirty: addressDirty, canSave: addressReady },
      phone: {
        ...statuses.phone,
        dirty: phoneDirty || consentPendingOnPhone,
        canSave: phoneReady,
      },
      emailConsent: statuses.emailConsent,
      phoneConsent: statuses.phoneConsent,
      autoSaveBallots: statuses.autoSaveBallots,
      language: statuses.language,
      navMode: statuses.navMode,
    }),
    [statuses, nameDirty, addressDirty, addressReady, phoneDirty, consentPendingOnPhone, phoneReady]
  );

  return {
    ...profileForm,
    firstName,
    lastName,
    setFirstName,
    setLastName,
    language,
    autoSaveBallots,
    savedAddress,
    savedPhone,
    settingsLoaded,
    groups,
    saveName,
    cancelName,
    saveAddress,
    cancelAddress,
    savePhone,
    cancelPhone,
    toggleEmailConsent,
    togglePhoneConsent,
    toggleAutoSaveBallots,
    selectLanguage,
    navMode,
    selectNavMode,
    clearAddressField,
    loadUserSettings,
  };
};

export default useSettingsSections;
